import { useEffect, useState } from 'react';
import { authenticatedApi } from '@/utils/authenticatedApi';
import { API_BASE_URL } from '@/utils/apiConfig';

/**
 * Fail-open per-tenant feature gate (mirrors the FinBOXX/PetsBOXX pattern).
 * Reads the tenant's SaaSBOXX-pushed feature map once and returns whether `code`
 * is enabled. ABSENT / loading / unfetchable => TRUE, so a feature is visible by
 * default and only hidden when the operator explicitly turns it off for the
 * tenant. Used to gate the LabBOXX-InTutor item (lab_ui_help_intutor).
 */
type FeatureMap = Record<string, boolean>;

let cache: FeatureMap | null = null;
// Break-glass recovery admins see every feature regardless of the tenant map.
// Backend-supplied (unspoofable); only widens what the UI shows.
let breakglass = false;
let inflight: Promise<FeatureMap> | null = null;

function load(): Promise<FeatureMap> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = authenticatedApi
      .get('/api/v1/platform/features/', { baseURL: API_BASE_URL })
      .then((r: { data?: { features?: FeatureMap; is_breakglass?: boolean } & FeatureMap }) => {
        breakglass = !!r.data?.is_breakglass;
        cache = (r.data?.features ?? (r.data as FeatureMap)) || {};
        return cache;
      })
      .catch(() => {
        cache = {};
        return cache as FeatureMap;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function useFeatureEnabled(code: string): boolean {
  const [features, setFeatures] = useState<FeatureMap>(cache ?? {});
  const [isBreakglass, setIsBreakglass] = useState<boolean>(breakglass);
  useEffect(() => {
    let alive = true;
    load().then((f) => {
      if (!alive) return;
      setFeatures(f);
      setIsBreakglass(breakglass);
    });
    return () => {
      alive = false;
    };
  }, []);
  if (isBreakglass) return true; // break-glass sees everything
  if (!(code in features)) return true; // fail-open
  return !!features[code];
}
